import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X as XIcon,
  Filter,
  Plus,
} from 'lucide-react';
import { Avatar } from '~/components/Avatar/Avatar';
import { LeadOriginBadges } from '~/components/Badge/Badge';
import { LeadDetailPanel } from '~/components/LeadDetailPanel/LeadDetailPanel';
import { LeadsViewToggle } from '~/components/LeadsViewToggle/LeadsViewToggle';
import { NewLeadModal } from '~/components/NewLeadModal/NewLeadModal';
import { TagsFilter, TagList } from '~/components/Tags';
import {
  usePipelines,
  usePipelineStages,
  useLeadsList,
  useDistinctTagsByTenant,
  parseTags,
  useTenantUsers,
} from '~/lib/queries';
import { useActiveTenantId, useTenantContext } from '~/lib/tenantContext';
import { currentUser } from '~/lib/pb';
import {
  EMAIL_ENGAGEMENT_FILTER_LABEL,
  type EmailEngagementLeadFilter,
} from '~/lib/email-engagement-settings';
import { EmailEngagementSettings } from '~/components/EmailEngagementSettings/EmailEngagementSettings';
import { useOportunidadesPeriod, resolvePeriod, tenantTimeZone } from '~/lib/dashboard-period';
import { PeriodFilter } from '~/components/PeriodFilter/PeriodFilter';
import { formatMoney, leadCurrency } from '~/lib/capabilities';
import { personDisplayName } from '~/lib/display-name';
import { prettyPhone } from '~/lib/phone';
import type { Lead, User } from '~/lib/types';
import styles from './_authed.oportunidades.module.css';

export const Route = createFileRoute('/_authed/oportunidades')({
  component: LeadsListPage,
});

const PIPELINE_STORAGE_KEY = 'focus_active_pipeline_by_tenant';
const PER_PAGE = 25;

function readPipelinePref(tenantId: string | null): string | null {
  if (!tenantId) return null;
  try {
    const map = JSON.parse(localStorage.getItem(PIPELINE_STORAGE_KEY) || '{}') as Record<
      string,
      string
    >;
    return map[tenantId] ?? null;
  } catch {
    return null;
  }
}

function writePipelinePref(tenantId: string, pipelineId: string) {
  try {
    const map = JSON.parse(localStorage.getItem(PIPELINE_STORAGE_KEY) || '{}') as Record<
      string,
      string
    >;
    map[tenantId] = pipelineId;
    localStorage.setItem(PIPELINE_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function userDisplayName(u: User | undefined): string {
  if (!u) return '—';
  return u.display_name || u.name || u.email || '—';
}

function contactDisplayName(lead: Lead): string {
  const c = lead.expand?.contact;
  if (!c) return '—';
  return personDisplayName(c) || (c.phone ? prettyPhone(c.phone) : null) || '—';
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'recién';
  if (diffMin < 60) return `hace ${diffMin}m`;
  if (diffH < 24) return `hace ${diffH}h`;
  if (diffD === 1) return 'ayer';
  if (diffD < 7) return `hace ${diffD}d`;
  return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short' }).format(d);
}

function LeadsListPage() {
  const tenantId = useActiveTenantId();
  const { selectedTenant } = useTenantContext();
  const [period, setPeriod] = useOportunidadesPeriod();
  const timeZone = tenantTimeZone(selectedTenant);
  const resolved = useMemo(() => resolvePeriod(period, { timeZone }), [period, timeZone]);
  const { data: pipelines } = usePipelines();
  const { data: users } = useTenantUsers();

  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [emailEngagementFilter, setEmailEngagementFilter] = useState<EmailEngagementLeadFilter>('');
  const [showNewLead, setShowNewLead] = useState<boolean>(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const { data: tagOptions, isLoading: tagsLoading } = useDistinctTagsByTenant();

  // Resolver pipeline por default desde localStorage o is_default.
  useEffect(() => {
    if (!pipelines || pipelines.length === 0) {
      setPipelineId(null);
      return;
    }
    const stored = readPipelinePref(tenantId);
    const picked =
      (stored && pipelines.find((p) => p.id === stored)) ||
      pipelines.find((p) => p.is_default) ||
      pipelines[0];
    setPipelineId(picked?.id ?? null);
  }, [pipelines, tenantId]);

  const { data: stages } = usePipelineStages(pipelineId ?? undefined);

  // Reset página al cambiar filtros
  useEffect(() => {
    setPage(1);
  }, [
    pipelineId,
    stageFilter,
    sourceFilter,
    ownerFilter,
    tagFilter,
    emailEngagementFilter,
    search,
    period,
  ]);

  const listSort = emailEngagementFilter !== '' ? '-email_engagement_at,-created' : '-created';

  const {
    data: result,
    isLoading,
    isError,
  } = useLeadsList({
    pipelineId: pipelineId ?? undefined,
    page,
    perPage: PER_PAGE,
    search: search.trim(),
    stageId: stageFilter || undefined,
    source: sourceFilter || undefined,
    assignedTo: ownerFilter || undefined,
    tags: tagFilter,
    sort: listSort,
    emailEngagement: emailEngagementFilter || undefined,
    createdFrom: resolved.start,
    createdTo: resolved.end,
  });

  const items = result?.items ?? [];
  const totalItems = result?.totalItems ?? 0;
  const totalPages = result?.totalPages ?? 1;

  // Derivar lista de sources únicos visibles (helper UI; no es exhaustivo del DB).
  const visibleSources = useMemo(() => {
    const set = new Set<string>();
    for (const l of items) if (l.source) set.add(l.source);
    return Array.from(set).sort();
  }, [items]);

  const activeFilterCount =
    (stageFilter ? 1 : 0) +
    (sourceFilter ? 1 : 0) +
    (ownerFilter ? 1 : 0) +
    (tagFilter.length > 0 ? 1 : 0) +
    (emailEngagementFilter ? 1 : 0);

  const me = currentUser();
  const canEditEngagementPrefs = me?.role === 'client_admin' && !!selectedTenant;

  function handlePipelineChange(id: string) {
    setPipelineId(id);
    if (tenantId) writePipelinePref(tenantId, id);
    setStageFilter('');
  }

  function clearAllFilters() {
    setStageFilter('');
    setSourceFilter('');
    setOwnerFilter('');
    setTagFilter([]);
    setEmailEngagementFilter('');
    setSearch('');
  }

  if (!pipelines || pipelines.length === 0) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className="h2">Oportunidades</h1>
        </header>
        <div className="empty-state">Aún no hay pipelines en este cliente.</div>
      </div>
    );
  }

  const activePipeline = pipelines.find((p) => p.id === pipelineId);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <div className={styles.headerTitle}>
            <h1 className="h2">Oportunidades</h1>
            {pipelines.length === 1 ? (
              <p className={styles.pipelineChip}>{activePipeline?.name}</p>
            ) : (
              <label className={styles.pipelineSelectWrap}>
                <span className={styles.pipelineChip}>
                  {activePipeline?.name}
                  <ChevronDown size={14} className={styles.chevron} aria-hidden />
                </span>
                <select
                  className={styles.nativeSelect}
                  value={pipelineId ?? ''}
                  onChange={(e) => handlePipelineChange(e.target.value)}
                  aria-label="Cambiar pipeline"
                >
                  {pipelines.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.is_default ? ' (principal)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.period}>
            <PeriodFilter value={period} onChange={setPeriod} />
          </div>
          <LeadsViewToggle active="list" />
          <div className={styles.totalBadge}>
            <strong>{totalItems}</strong>
            <span className={styles.muted}>
              {totalItems === 1 ? 'oportunidad' : 'oportunidades'}
            </span>
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setShowNewLead(true)}>
            <Plus size={14} aria-hidden /> Nueva oportunidad
          </button>
        </div>
      </header>

      {showNewLead && (
        <NewLeadModal onClose={() => setShowNewLead(false)} pipelineId={pipelineId ?? undefined} />
      )}

      {/* Toolbar: search + filters */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={14} aria-hidden />
          <input
            type="text"
            placeholder="Buscar por nombre, email, empresa, título…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          {search && (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
            >
              <XIcon size={14} />
            </button>
          )}
        </div>
        <button
          type="button"
          className={styles.filterToggle}
          onClick={() => setShowFilters((s) => !s)}
        >
          <Filter size={14} aria-hidden />
          Filtros
          {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
        </button>
        {(activeFilterCount > 0 || search) && (
          <button type="button" className={styles.clearAllBtn} onClick={clearAllFilters}>
            Limpiar todo
          </button>
        )}
      </div>

      {showFilters && (
        <div className={styles.filtersPanel}>
          <label className={styles.filterField}>
            <span className={styles.filterField__label}>Etapa</span>
            <select
              className={styles.filterSelect}
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
            >
              <option value="">Todas</option>
              {(stages ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterField__label}>Origen</span>
            <select
              className={styles.filterSelect}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {visibleSources.map((src) => (
                <option key={src} value={src}>
                  {src}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterField__label}>Asignado a</span>
            <select
              className={styles.filterSelect}
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
            >
              <option value="">Cualquiera</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {userDisplayName(u)}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.filterField}>
            <span className={styles.filterField__label}>Tags</span>
            <TagsFilter
              options={tagOptions ?? []}
              selected={tagFilter}
              onChange={setTagFilter}
              isLoading={tagsLoading}
            />
          </div>
          <label className={styles.filterField}>
            <span className={styles.filterField__label}>Correo enviado</span>
            <select
              className={styles.filterSelect}
              value={emailEngagementFilter}
              onChange={(e) =>
                setEmailEngagementFilter(e.target.value as EmailEngagementLeadFilter)
              }
            >
              <option value="">Sin filtro</option>
              {(
                Object.keys(EMAIL_ENGAGEMENT_FILTER_LABEL) as Array<
                  Exclude<EmailEngagementLeadFilter, ''>
                >
              ).map((key) => (
                <option key={key} value={key}>
                  {EMAIL_ENGAGEMENT_FILTER_LABEL[key]}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Tabla */}
      {isLoading ? (
        <div className="empty-state">Cargando…</div>
      ) : isError ? (
        <div className="empty-state">
          No pudimos cargar las oportunidades. Recarga o intenta más tarde.
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          {search || activeFilterCount > 0
            ? 'No hay oportunidades que coincidan con los filtros.'
            : 'Aún no hay oportunidades en este pipeline.'}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Oportunidad</th>
                <th>Etapa</th>
                <th className={styles.numeric}>Valor</th>
                <th className={styles.mobileHide}>Asignado</th>
                <th className={styles.mobileHide}>Origen</th>
                <th className={styles.mobileHide}>Tags</th>
                <th className={`${styles.numeric} ${styles.mobileHide}`}>Creada</th>
              </tr>
            </thead>
            <tbody>
              {items.map((lead) => {
                const stage = lead.expand?.stage;
                const owner = lead.expand?.assigned_to;
                const name = contactDisplayName(lead);
                return (
                  <tr key={lead.id} className={styles.row} onClick={() => setSelectedLead(lead)}>
                    <td>
                      <div className={styles.leadCell}>
                        <Avatar name={name} size="sm" />
                        <div className={styles.leadCell__meta}>
                          <span className={styles.leadCell__title}>
                            {lead.title || 'Sin título'}
                          </span>
                          <span className={styles.leadCell__contact}>
                            {name}
                            {lead.expand?.contact?.company && <> · {lead.expand.contact.company}</>}
                          </span>
                          {lead.email_engagement_kind && (
                            <span
                              className={`${styles.engagementChip} ${styles[`engagementChip--${lead.email_engagement_kind}`]}`}
                            >
                              {lead.email_engagement_kind === 'clicked'
                                ? 'Clic en correo'
                                : 'Abrió correo'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {stage && (
                        <span className={styles.stageBadge}>
                          <span
                            className={styles.stageBadge__dot}
                            style={{ background: stage.color || 'var(--color-text-subtle)' }}
                            aria-hidden
                          />
                          {stage.name}
                        </span>
                      )}
                    </td>
                    <td className={styles.numeric}>
                      {formatMoney(lead.value, leadCurrency(lead, selectedTenant))}
                    </td>
                    <td className={styles.mobileHide}>
                      {owner ? userDisplayName(owner) : <span className={styles.muted}>—</span>}
                    </td>
                    <td className={styles.mobileHide}>
                      <LeadOriginBadges
                        source={lead.source}
                        tags={parseTags((lead as unknown as { tags?: unknown }).tags)}
                      />
                    </td>
                    <td className={styles.mobileHide}>
                      {(() => {
                        const tags = parseTags((lead as unknown as { tags?: unknown }).tags);
                        return tags.length > 0 ? (
                          <TagList tags={tags} max={2} />
                        ) : (
                          <span className={styles.muted}>—</span>
                        );
                      })()}
                    </td>
                    <td className={`${styles.numeric} ${styles.muted} ${styles.mobileHide}`}>
                      {formatRelativeDate(lead.created)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.paginationBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            aria-label="Página anterior"
          >
            <ChevronLeft size={14} />
            Anterior
          </button>
          <span className={styles.paginationInfo}>
            Página <strong>{page}</strong> de <strong>{totalPages}</strong>
          </span>
          <button
            type="button"
            className={styles.paginationBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            aria-label="Página siguiente"
          >
            Siguiente
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {canEditEngagementPrefs && selectedTenant && (
        <section className={styles.prefsSection} id="email-engagement-prefs">
          <EmailEngagementSettings tenant={selectedTenant} variant="tenant_admin" />
        </section>
      )}

      <LeadDetailPanel
        lead={selectedLead}
        stages={stages ?? []}
        open={!!selectedLead}
        onOpenChange={(open) => {
          if (!open) setSelectedLead(null);
        }}
      />
    </div>
  );
}
